"use client";

import { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";

/**
 * Geanimeerde KPI-meter. Telt van `from` naar `to` met easeOut over `durationMs`,
 * getriggerd zodra de meter in beeld komt (eenmalig). De balk volgt de waarde
 * t.o.v. `max` — dus omhoog (matchingspercentage) of omlaag (doorlooptijd).
 * Sober/monochroom per brand book; respecteert prefers-reduced-motion.
 */

type Props = {
  title: string;
  from: number;
  to: number;
  max: number; // noemer voor de balk
  decimals?: number;
  suffix?: string;
  durationMs?: number;
  tone?: "ink" | "brand" | "amber"; // huiskleur voor getal + balk
};

const TONE = {
  ink: { value: "text-[var(--ink)]", suffix: "text-[var(--ink-2)]", bar: "bg-[var(--ink)]" },
  brand: { value: "text-[var(--brand)]", suffix: "text-[var(--brand)]", bar: "bg-[var(--brand)]" },
  amber: {
    value: "text-[var(--pool-algemeen)]",
    suffix: "text-[var(--pool-algemeen)]",
    bar: "bg-[var(--pool-algemeen)]",
  },
} as const;

function formatNL(v: number, decimals: number) {
  return v.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export default function KpiMeter({
  title,
  from,
  to,
  max,
  decimals = 0,
  suffix = "",
  durationMs = 20000,
  tone = "ink",
}: Props) {
  const c = TONE[tone];
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduce = useReducedMotion();
  const [value, setValue] = useState(from);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setValue(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduce, from, to, durationMs]);

  const pct = Math.max(0, Math.min(100, (value / max) * 100));

  return (
    <div ref={ref} className="flex-1 py-4 first:pt-0 last:pb-0 sm:px-10 sm:py-0 sm:first:pl-0 sm:last:pr-0">
      {/* Mobiel: titel en waarde naast elkaar — gestapeld kost drie regels per meter. */}
      <div className="flex items-baseline justify-between gap-3 sm:block">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:mb-3 sm:text-[13px]">
          {title}
        </p>
        <p
          className={`flex shrink-0 items-baseline gap-1 text-[26px] font-extrabold tracking-[-0.03em] tabular-nums sm:text-[clamp(30px,4.5vw,42px)] ${c.value}`}
        >
          {formatNL(value, decimals)}
          {suffix && (
            <span className={`text-[14px] font-semibold sm:text-[clamp(15px,2vw,20px)] ${c.suffix}`}>
              {suffix}
            </span>
          )}
        </p>
      </div>
      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-sunk)] sm:mt-4">
        <div
          className={`h-full rounded-full ${c.bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
